import React from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { API_DATE_FORMAT, DATE_FORMAT } from '@app/constants/dateFormats';
import { CustomerPointsSummaryData } from '@app/interfaces/customer';
import { formatMoney } from '@app/utils/utils';

interface Props {
  summary?: CustomerPointsSummaryData | null;
  /** Giá trị 1 điểm (₫), để quy số điểm dùng được ra tiền. */
  pointValue?: number | null;
  /** Bản gọn cho thẻ khách ở form đơn. */
  compact?: boolean;
  className?: string;
}

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; compact?: boolean }> = ({
  label,
  value,
  hint,
  compact,
}) => (
  <div
    className={clsx(
      'tw-min-w-0 tw-rounded-md tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-subtle tw-text-center',
      compact ? 'tw-px-1 tw-py-1.5' : 'tw-px-3 tw-py-2',
    )}
  >
    <div className="tw-mb-0.5 tw-text-[11px] tw-text-on-surface-variant">{label}</div>
    <div className="tw-text-[13px] tw-font-bold tw-text-primary">{value}</div>
    {hint && <div className="tw-text-[11px] tw-text-on-surface-variant">{hint}</div>}
  </div>
);

/** Điểm của khách trong một cái nhìn: dùng được bao nhiêu, năm nay tích / dùng bao nhiêu, đã reset bao nhiêu. */
const CustomerPointsSummary: React.FC<Props> = ({ summary, pointValue, compact, className }) => {
  if (!summary) return null;

  const value = Number(pointValue) || 0;
  const expiry = moment(summary.expires_on, API_DATE_FORMAT).format(DATE_FORMAT);
  const usableHint = compact ? undefined : value > 0 ? `= ${formatMoney(Math.round(summary.usable * value))}` : undefined;

  return (
    <div className={className}>
      <div className={clsx('tw-grid tw-gap-2', compact ? 'tw-grid-cols-4' : 'tw-grid-cols-2 md:tw-grid-cols-4')}>
        <Stat compact={compact} label="Dùng được" value={summary.usable} hint={usableHint} />
        <Stat compact={compact} label={`Tích ${summary.year}`} value={summary.earned} />
        <Stat compact={compact} label={`Đã dùng ${summary.year}`} value={summary.used} />
        <Stat compact={compact} label="Đã reset" value={summary.expired} />
      </div>
      <div className="tw-mt-1 tw-text-[11px] tw-text-on-surface-variant">
        Hạn dùng đến {expiry}
        {summary.reserved > 0 && ` · ${summary.reserved} điểm đang giữ ở đơn chưa hoàn tất`}
      </div>
    </div>
  );
};

export default CustomerPointsSummary;
